import { XIcon } from 'lucide-react';
import { useCallback, useMemo, forwardRef, useState, useRef, useEffect } from 'react';

import { Kbd } from '@/components/kbd';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { StateCell } from '@/features/pulls/components/pull-list';
import { FilterDropdownItems } from '@/features/pulls/components/pulls-filter';
import { FilterOperation, FilterField } from '@/types/filters';

type FocusTarget = 'badge' | 'operation' | 'value';

export const FilterBadge = forwardRef<
  HTMLDivElement,
  {
    field: FilterField;
    values: string[];
    renderOption: (option: { value: string; label: string }) => React.ReactNode;
    operation: FilterOperation;
    onOperationChange: (operation: FilterOperation) => void;
    onToggleValue: (value: string) => void;
    onRemove: () => void;
    options: Array<{ value: string; label: string }>;
    authors: Array<{ login: string; avatarUrl?: string }>;
    reviewers?: Array<{ login: string; avatarUrl?: string }>;
    tabIndex?: number;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    onEscape?: () => void;
  }
>(
  (
    {
      field,
      values,
      renderOption,
      operation,
      onOperationChange,
      onToggleValue,
      onRemove,
      options,
      authors,
      reviewers,
      tabIndex,
      onKeyDown,
      onEscape,
    },
    ref
  ) => {
    const hasMultipleValues = useMemo(() => values.length > 1, [values]);
    const [operationMenuOpen, setOperationMenuOpen] = useState(false);
    const [valueMenuOpen, setValueMenuOpen] = useState(false);
    const [focusTarget, setFocusTarget] = useState<FocusTarget>('badge');

    const operationButtonRef = useRef<HTMLButtonElement>(null);
    const valueButtonRef = useRef<HTMLButtonElement>(null);

    const operationLabel = useCallback(
      (operation: FilterOperation) => {
        if (operation === 'IN') {
          if (hasMultipleValues) return 'is any of';
          return 'is';
        }
        if (operation === 'NOT IN') {
          if (hasMultipleValues) return 'is none of';
          return 'is not';
        }
        return operation;
      },
      [hasMultipleValues]
    );

    // Focus the appropriate element when focus target changes
    useEffect(() => {
      if (focusTarget === 'operation') {
        operationButtonRef.current?.focus();
      } else if (focusTarget === 'value') {
        valueButtonRef.current?.focus();
      }
    }, [focusTarget]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Handle navigation within the badge
        if (focusTarget !== 'badge') {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setFocusTarget('badge');
            setOperationMenuOpen(false);
            setValueMenuOpen(false);
            // Focus back on the badge
            (ref as React.RefObject<HTMLDivElement>)?.current?.focus();
            return;
          }

          if (e.key === 'Tab' && focusTarget === 'operation') {
            e.preventDefault();
            setFocusTarget('value');
            return;
          }

          if (e.key === 'Tab' && e.shiftKey && focusTarget === 'value') {
            e.preventDefault();
            setFocusTarget('operation');
            return;
          }

          // Let the dropdown handle Enter when menus are open
          if (e.key === 'Enter' && (operationMenuOpen || valueMenuOpen)) {
            return;
          }

          if (e.key === 'Enter') {
            e.preventDefault();
            if (focusTarget === 'operation') {
              setOperationMenuOpen(true);
            } else if (focusTarget === 'value') {
              setValueMenuOpen(true);
            }
            return;
          }

          // Don't process other shortcuts when in sub-focus mode
          return;
        }

        // Badge-level keyboard shortcuts
        if (e.key === 'Enter') {
          e.preventDefault();
          setFocusTarget('operation');
          return;
        }

        if (e.key === 'Escape') {
          e.preventDefault();
          onEscape?.();
          return;
        }

        // First call the parent's keydown handler for Tab navigation between badges
        onKeyDown?.(e);

        // Handle our own keyboard shortcuts
        if (e.key === 'o' && !operationMenuOpen && !valueMenuOpen) {
          e.preventDefault();
          setOperationMenuOpen(true);
        } else if (e.key === 'v' && !operationMenuOpen && !valueMenuOpen) {
          e.preventDefault();
          setValueMenuOpen(true);
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          onRemove();
        }
      },
      [onKeyDown, focusTarget, operationMenuOpen, valueMenuOpen, onRemove, ref, onEscape]
    );

    if (values.length === 0) return null;

    return (
      <div
        ref={ref}
        className="flex items-center h-8 overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md relative"
        tabIndex={tabIndex}
        onKeyDown={handleKeyDown}
        title={
          focusTarget === 'badge'
            ? "Press 'Enter' to edit, 'o' for operation, 'v' for values, 'Delete' to remove"
            : "Press 'Tab' to navigate, 'Enter' to open, 'Esc' to go back"
        }
      >
        <div className="flex items-center border border-border border-r-0 rounded-l-md bg-white h-8 px-2">
          <span className="text-xs capitalize">{field}</span>
        </div>

        <Separator orientation="vertical" className="h-8" />
        <DropdownMenu
          open={operationMenuOpen}
          onOpenChange={(open) => {
            setOperationMenuOpen(open);
            if (!open) {
              setFocusTarget('badge');
              // Refocus the badge
              (ref as React.RefObject<HTMLDivElement>)?.current?.focus();
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              ref={operationButtonRef}
              variant="outline"
              size="sm"
              className={`h-8 font-normal rounded-none hover:bg-accent transition-all px-2 border-x-0 ${
                focusTarget === 'operation' ? 'ring-2 ring-ring ring-inset' : ''
              }`}
              tabIndex={-1}
            >
              <span className="text-xs">{operationLabel(operation)}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[120px]">
            <DropdownMenuItem
              className="text-xs flex items-center justify-between"
              onClick={() => onOperationChange('IN')}
            >
              <span>{operationLabel('IN')}</span>
              {operation !== 'IN' && <Kbd className="ml-2">↵</Kbd>}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-xs flex items-center justify-between"
              onClick={() => onOperationChange('NOT IN')}
            >
              <span>{operationLabel('NOT IN')}</span>
              {operation !== 'NOT IN' && <Kbd className="ml-2">↵</Kbd>}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Separator orientation="vertical" className="h-8" />
        <DropdownMenu
          open={valueMenuOpen}
          onOpenChange={(open) => {
            setValueMenuOpen(open);
            if (!open) {
              setFocusTarget('badge');
              // Refocus the badge
              (ref as React.RefObject<HTMLDivElement>)?.current?.focus();
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              ref={valueButtonRef}
              variant="outline"
              size="sm"
              className={`h-8 px-2 rounded-none font-normal hover:bg-accent transition-colors flex items-center gap-1 border-x-0 ${
                focusTarget === 'value' ? 'ring-2 ring-ring ring-inset' : ''
              }`}
              tabIndex={-1}
            >
              {field === 'state' &&
                (values.length > 1 ? (
                  <div className="flex items-center gap-1">
                    <div className="flex -space-x-1 mr-1">
                      {values.map((value) => {
                        const option = options.find((opt) => opt.value === value);
                        if (!option?.value) return null;
                        return (
                          <div key={value} className="size-4 flex items-center justify-center rounded-full bg-stone-50">
                            <StateCell state={option.value as 'OPEN' | 'CLOSED' | 'MERGED' | 'DRAFT'} />
                          </div>
                        );
                      })}
                    </div>
                    <span className="text-xs">{values.length} states</span>
                  </div>
                ) : (
                  values.map((value) => {
                    const option = options.find((opt) => opt.value === value);
                    if (!option?.value) return null;
                    return (
                      <div key={value} className="flex items-center gap-1">
                        <StateCell state={option.value as 'OPEN' | 'CLOSED' | 'MERGED' | 'DRAFT'} />
                        <span className="text-xs">{option.label}</span>
                      </div>
                    );
                  })
                ))}
              {field === 'repo' &&
                (values.length > 1 ? (
                  <span className="text-xs">{values.length} repos</span>
                ) : (
                  <span className="text-xs">{values.join(', ')}</span>
                ))}
              {field === 'author' &&
                authors &&
                (values.length > 1 ? (
                  <div className="flex items-center gap-1">
                    <div className="flex -space-x-1 mr-1">
                      {values.slice(0, 3).map((value) => {
                        const author = authors.find((a) => a.login === value);
                        return (
                          <img
                            key={value}
                            src={author?.avatarUrl}
                            alt={`${value}'s avatar`}
                            className="size-4 rounded-full ring-2 ring-background"
                          />
                        );
                      })}
                    </div>
                    <span className="text-xs">{values.length} authors</span>
                  </div>
                ) : (
                  values.map((value) => {
                    const author = authors.find((a) => a.login === value);
                    return (
                      <div key={value} className="flex items-center gap-1">
                        <img
                          src={author?.avatarUrl}
                          alt={`${value}'s avatar`}
                          className="size-4 rounded-full shrink-0"
                        />
                        <span className="text-xs">{value}</span>
                      </div>
                    );
                  })
                ))}
              {field === 'reviewer' &&
                reviewers &&
                (values.length > 1 ? (
                  <div className="flex items-center gap-1">
                    <div className="flex -space-x-1 mr-1">
                      {values.slice(0, 3).map((value) => {
                        const reviewer = reviewers.find((r) => r.login === value);
                        return (
                          <img
                            key={value}
                            src={reviewer?.avatarUrl}
                            alt={`${value}'s avatar`}
                            className="size-4 rounded-full ring-2 ring-background"
                          />
                        );
                      })}
                    </div>
                    <span className="text-xs">{values.length} reviewers</span>
                  </div>
                ) : (
                  values.map((value) => {
                    const reviewer = reviewers.find((r) => r.login === value);
                    return (
                      <div key={value} className="flex items-center gap-1">
                        <img
                          src={reviewer?.avatarUrl}
                          alt={`${value}'s avatar`}
                          className="size-4 rounded-full shrink-0"
                        />
                        <span className="text-xs">{value}</span>
                      </div>
                    );
                  })
                ))}
              {field === 'readyToMerge' &&
                <span className="text-xs">{values[0] === 'true' ? 'Ready' : 'Not ready'}</span>
              }
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[220px]">
            <FilterDropdownItems
              options={options}
              renderOption={renderOption}
              values={values}
              toggleValue={onToggleValue}
            />
          </DropdownMenuContent>
        </DropdownMenu>

        <Separator orientation="vertical" className="h-8" />

        <Button
          variant="outline"
          size="icon"
          className="h-8 w-7 p-0 rounded-l-none rounded-r-md bg-white hover:bg-accent transition-colors border-l-0"
          onClick={onRemove}
          tabIndex={-1}
        >
          <XIcon className="h-3 w-3" />
        </Button>
      </div>
    );
  }
);

FilterBadge.displayName = 'FilterBadge';
